import React, { useEffect, useState } from "react";
import { Plus, Trash2, Tag, Pencil, X, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { fetchCategories, createCategory, updateCategory, deleteCategory, addSubcategory, deleteSubcategory } from "../lib/api";

export default function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [newSub, setNewSub] = useState({});
  const [editing, setEditing] = useState({}); // { [id]: name }

  const load = () => fetchCategories().then(setCats);
  useEffect(() => { load(); }, []);

  const handleAddCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await createCategory({ name: newCat.trim(), subcategories: [] });
      setNewCat("");
      load();
      toast.success("Categoria creata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const handleRenameCategory = async (id) => {
    const name = editing[id];
    if (!name?.trim()) return;
    try {
      await updateCategory(id, { name: name.trim() });
      setEditing((s) => ({ ...s, [id]: undefined }));
      load();
      toast.success("Categoria rinominata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const handleChangeColor = async (id, color) => {
    // Optimistic UI
    setCats((prev) => prev.map((c) => (c.id === id ? { ...c, color } : c)));
    try {
      await updateCategory(id, { color });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio colore");
      load();
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Eliminare la categoria "${name}"?`)) return;
    try {
      await deleteCategory(id);
      load();
      toast.success("Categoria eliminata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const handleAddSub = async (id) => {
    const v = (newSub[id] || "").trim();
    if (!v) return;
    try {
      await addSubcategory(id, v);
      setNewSub((s) => ({ ...s, [id]: "" }));
      load();
      toast.success("Sotto-categoria aggiunta");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const handleDelSub = async (id, sub) => {
    if (!window.confirm(`Eliminare la sotto-categoria "${sub}"?`)) return;
    try {
      await deleteSubcategory(id, sub);
      load();
      toast.success("Sotto-categoria eliminata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-8" data-testid="categories-admin-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Impostazioni</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight">Categorie</h1>
        <p className="text-stone-600 mt-2 max-w-xl">Personalizza categorie e sotto-categoria utilizzate nelle spese.</p>
      </div>

      <div className="card-soft p-6 flex items-center gap-3" data-testid="new-category-row">
        <Tag size={18} strokeWidth={1.5} className="text-stone-500" />
        <Input
          value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          placeholder="Nome nuova categoria"
          className="rounded-xl max-w-sm"
          data-testid="new-category-input"
          onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
        />
        <Button onClick={handleAddCategory} className="rounded-full bg-[#377D22] hover:bg-[#2E5F1B]" data-testid="new-category-add">
          <Plus size={16} className="mr-1" /> Aggiungi
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {cats.map((c) => (
          <div key={c.id} className="card-soft p-6" data-testid={`category-card-${c.name}`}>
            <div className="flex items-center justify-between gap-3 mb-4">
              {editing[c.id] !== undefined ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={editing[c.id]}
                    onChange={(e) => setEditing((s) => ({ ...s, [c.id]: e.target.value }))}
                    className="rounded-xl"
                    data-testid={`rename-input-${c.id}`}
                  />
                  <button className="pill-btn p-2 hover:bg-stone-100" onClick={() => handleRenameCategory(c.id)} data-testid={`rename-save-${c.id}`}>
                    <Check size={16} />
                  </button>
                  <button className="pill-btn p-2 hover:bg-stone-100" onClick={() => setEditing((s) => ({ ...s, [c.id]: undefined }))} data-testid={`rename-cancel-${c.id}`}>
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label
                    className="relative inline-block w-6 h-6 rounded-full border border-stone-200 cursor-pointer shadow-sm hover:scale-110 transition-transform"
                    style={{ background: c.color || "#64748B" }}
                    title="Scegli colore"
                    data-testid={`color-swatch-${c.id}`}
                  >
                    <input
                      type="color"
                      value={c.color || "#64748B"}
                      onChange={(e) => handleChangeColor(c.id, e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      data-testid={`color-input-${c.id}`}
                    />
                  </label>
                  <h3 className="font-display text-xl font-semibold">{c.name}</h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-500">{c.subcategories.length} sotto</span>
                </div>
              )}
              {editing[c.id] === undefined && (
                <div className="flex items-center gap-1">
                  <button className="pill-btn p-2 hover:bg-stone-100" onClick={() => setEditing((s) => ({ ...s, [c.id]: c.name }))} data-testid={`rename-${c.id}`}>
                    <Pencil size={15} strokeWidth={1.5} />
                  </button>
                  <button className="pill-btn p-2 hover:bg-red-50 text-red-600" onClick={() => handleDeleteCategory(c.id, c.name)} data-testid={`delete-cat-${c.id}`}>
                    <Trash2 size={15} strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {c.subcategories.length === 0 && <span className="text-sm text-stone-400">Nessuna sotto-categoria</span>}
              {c.subcategories.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-stone-100 text-sm text-stone-700" data-testid={`sub-chip-${c.id}-${s}`}>
                  {s}
                  <button className="pill-btn p-1 hover:bg-red-100 text-red-600 ml-1 rounded-full" onClick={() => handleDelSub(c.id, s)} data-testid={`del-sub-${c.id}-${s}`}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Input
                value={newSub[c.id] || ""}
                onChange={(e) => setNewSub((s) => ({ ...s, [c.id]: e.target.value }))}
                placeholder="Nuova sotto-categoria"
                className="rounded-xl"
                data-testid={`new-sub-input-${c.id}`}
                onKeyDown={(e) => e.key === "Enter" && handleAddSub(c.id)}
              />
              <Button onClick={() => handleAddSub(c.id)} variant="outline" className="rounded-full" data-testid={`new-sub-add-${c.id}`}>
                <Plus size={14} className="mr-1" /> Aggiungi
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
